import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Empty, Table, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { CalendarClock, CheckCircle2, Hourglass, UserCheck } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import {
  PROBATION_WARN_DAYS, branchById, daysBetween, empById, fmtD, fmtMoney, isWorking, missingDocs, positionById, reqById, toDay,
} from './hrLogic';
import { probationStats } from './hrInsights';
import { Badge, EmpCell, Kpi, PageHeader, Panel, empUrl, useHrPerms } from './hrUi';
import { ContractModal, EvalModal } from './hrModals';
import CandidateDrawer from './CandidateDrawer';

/** Thử việc: theo dõi hạn, đánh giá đạt/không đạt, chuẩn bị người sắp nhận việc */
export default function Probation() {
  const s = useHr();
  const navigate = useNavigate();
  const { canEdit } = useHrPerms();
  const today = toDay();
  const [evalFor, setEvalFor] = useState<string | null>(null);
  const [contractFor, setContractFor] = useState<string | null>(null);
  const [cand, setCand] = useState<string | null>(null);

  const rows = useMemo(() => s.contracts.filter(c => c.kind === 'probation' && !c.cancelled && !c.evaluation).map(c => {
    const e = empById(s, c.empId)!;
    const total = daysBetween(c.start, c.end!) + 1;
    const done = Math.min(total, Math.max(0, daysBetween(c.start, today) + 1));
    return { c, e, total, done, left: daysBetween(today, c.end!), miss: missingDocs(s, e) };
  }).filter(r => r.e && isWorking(r.e, today) && !r.e.leftDate && r.c.start <= today).sort((a, b) => a.left - b.left), [s, today]);
  const stats = probationStats(s, today);
  const recent = s.contracts.filter(c => c.kind === 'probation' && c.evaluation && c.evaluation.at >= dayjs().subtract(120, 'day').valueOf())
    .sort((a, b) => b.evaluation!.at - a.evaluation!.at);
  const upcoming = s.candidates.filter(c => c.outcome === 'open' && c.offer?.status === 'accepted');
  const futureHires = s.employees.filter(e => e.hireDate > today);

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Thử việc" desc={`Nhắc đánh giá trước ${PROBATION_WARN_DAYS} ngày · thử việc không được gia hạn · lương thử việc tối thiểu 85% lương chính thức`} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={<Hourglass size={18} />} tone="bg-amber-50 text-amber-600" label="Đang thử việc" value={rows.length} />
          <Kpi icon={<CalendarClock size={18} />} tone="bg-orange-50 text-orange-600" label={`Hết hạn trong ${PROBATION_WARN_DAYS} ngày`} value={rows.filter(r => r.left >= 0 && r.left <= PROBATION_WARN_DAYS).length} />
          <Kpi icon={<CalendarClock size={18} />} tone="bg-rose-50 text-rose-600" label="Quá hạn chưa đánh giá" value={rows.filter(r => r.left < 0).length} />
          <Kpi icon={<CheckCircle2 size={18} />} tone="bg-emerald-50 text-emerald-600" label="Tỷ lệ đạt 12 tháng" value={stats.pass + stats.fail ? `${stats.rate}%` : '—'} hint={`${stats.pass} đạt · ${stats.fail} không đạt`} />
        </div>

        <Panel title="Đang thử việc">
          <Table size="middle" rowKey={r => r.c.id} dataSource={rows} pagination={false} scroll={{ x: 960 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có ai đang thử việc" /> }}
            columns={[
              { title: 'Nhân sự', key: 'emp', width: 260, render: (_, r) => <EmpCell s={s} e={r.e} /> },
              { title: 'Thời gian thử việc', key: 'term', width: 230, render: (_, r) => (
                <div>
                  <div className="text-xs">{fmtD(r.c.start)} – {fmtD(r.c.end)} ({r.total} ngày)</div>
                  <div className="h-1.5 mt-1 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${r.left < 0 ? 'bg-rose-500' : r.left <= PROBATION_WARN_DAYS ? 'bg-amber-500' : 'bg-sky-500'}`} style={{ width: `${(r.done / r.total) * 100}%` }} /></div>
                </div>
              ) },
              { title: 'Còn lại', key: 'left', width: 120, render: (_, r) => <span className={r.left < 0 ? 'text-rose-600 font-medium' : r.left <= PROBATION_WARN_DAYS ? 'text-amber-600 font-medium' : ''}>{r.left < 0 ? `quá ${-r.left} ngày` : `${r.left} ngày`}</span> },
              { title: 'Lương TV / chính thức', key: 'sal', width: 190, render: (_, r) => <div className="text-xs">{fmtMoney(r.c.salary)}<div className="text-gray-400">{fmtMoney(r.c.officialSalary || 0)}</div></div> },
              { title: 'Hồ sơ', key: 'docs', width: 170, render: (_, r) => (
                <div className="flex flex-wrap gap-1">
                  {!r.c.signedAt && <Badge className="bg-rose-50 text-rose-700 border-rose-200">Thỏa thuận chưa ký</Badge>}
                  {r.miss.length > 0 ? <Tooltip title={r.miss.join(', ')}><span><Badge className="bg-amber-50 text-amber-700 border-amber-200">Thiếu {r.miss.length} giấy tờ</Badge></span></Tooltip> : r.c.signedAt && <span className="text-xs text-emerald-600">Đủ</span>}
                </div>
              ) },
              { title: '', key: 'act', width: 150, render: (_, r) => (
                <div className="flex gap-1">
                  {canEdit && <Button size="small" type={r.left <= PROBATION_WARN_DAYS ? 'primary' : 'default'} onClick={() => setEvalFor(r.c.id)}>Đánh giá</Button>}
                  <Button size="small" type="text" onClick={() => navigate(empUrl(r.e.id, 'contracts'))}>Hồ sơ</Button>
                </div>
              ) },
            ]} />
        </Panel>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <Panel title="Sắp nhận việc" icon={<UserCheck size={15} className="text-indigo-500" />}>
            {upcoming.length + futureHires.length === 0 ? <Empty className="py-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có" /> : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {upcoming.map(c => {
                  const r = reqById(s, c.requisitionId);
                  return (
                    <button key={c.id} type="button" onClick={() => setCand(c.id)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 flex items-center justify-between gap-2">
                      <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-gray-500">{positionById(s, r?.positionId)?.name} · {branchById(s, r?.branchId)?.short} · offer {fmtMoney(c.offer!.salary)}</div></div>
                      <div className="text-xs text-indigo-600 whitespace-nowrap">nhận việc {fmtD(c.offer!.startDate)}</div>
                    </button>
                  );
                })}
                {futureHires.map(e => (
                  <button key={e.id} type="button" onClick={() => navigate(empUrl(e.id))} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 flex items-center justify-between gap-2">
                    <div><div className="text-sm font-medium">{e.name}</div><div className="text-xs text-gray-500">{positionById(s, e.positionId)?.name} · {branchById(s, e.branchId)?.short}</div></div>
                    <div className="text-xs text-indigo-600 whitespace-nowrap">vào làm {fmtD(e.hireDate)}</div>
                  </button>
                ))}
              </div>
            )}
          </Panel>
          <Panel title="Kết quả thử việc 120 ngày qua">
            {recent.length === 0 ? <Empty className="py-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có" /> : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {recent.map(c => {
                  const e = empById(s, c.empId);
                  return (
                    <button key={c.id} type="button" onClick={() => e && navigate(empUrl(e.id, 'contracts'))} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{e?.name}</span>
                        <Badge className={c.evaluation!.result === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}>
                          {c.evaluation!.result === 'pass' ? 'Đạt' : 'Không đạt'}{c.evaluation!.score ? ` · ${c.evaluation!.score}/10` : ''}
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-500">{positionById(s, e?.positionId)?.name} · {branchById(s, e?.branchId)?.short} · {dayjs(c.evaluation!.at).format('DD/MM/YYYY')} · {c.evaluation!.by}</div>
                      {c.evaluation!.note && <div className="text-xs text-gray-600 mt-0.5">{c.evaluation!.note}</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>
      </div>
      {evalFor && <EvalModal contractId={evalFor} onClose={() => setEvalFor(null)} onPassed={id => setContractFor(id)} />}
      {contractFor && <ContractModal empId={contractFor} onClose={() => setContractFor(null)} />}
      <CandidateDrawer id={cand} onClose={() => setCand(null)} />
    </Layout>
  );
}
